import { DOCUMENT, Location } from '@angular/common';
import { Component, Inject, OnInit, NgZone, ViewChild, ViewContainerRef, AfterViewInit } from '@angular/core';
import {
  NavigationCancel,
  Event as NavigationEvent,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import { logger } from '@core/helpers/logger';
import {
  MetaService,
  LanguageService,
  AuthService,
  ContextService,
  ConfigService,
  AnalyticsService,
  BillingService,
  CrispChatService,
  ScreenService,
  RequestService,
  PlatformService,
} from '@core/services/';
import { WebsocketService } from '@core/websocket';
import { DestroyableComponent } from '@models/destroyable.component';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { ErrorNotificationService, SubscribeErrorType } from '@shared/error-notification/error-notification.service';
import { loadBillingSetting, loadProfileSettings } from '@store/actions/data.actions';
import { setLanguages, setProfile } from '@store/actions/profile.actions';
import { getMyProfile } from '@store/reducers/profile.reducer';
import { User, Language, UserProfileVerificationEnum } from 'lingo2-models';
import { NgEventBus } from 'ng-event-bus';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, Observable, of, filter, takeUntil, tap, map, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, pluck } from 'rxjs/operators';
import { ModalService } from './core/services/modal.services';
import { TranslateService } from '@ngx-translate/core';
import { TranslationCacheService } from './core/services/translation-cache.service';

interface WebSocketMessage {
  account_id: string;
  verification_status?: string;
  entity?: string;
  version?: string;
}

interface WebSocketSubscription {
  stream: Observable<any>;
  handler: (message: WebSocketMessage) => void;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends DestroyableComponent implements OnInit, AfterViewInit {
  @ViewChild('View__Overflow', { read: ViewContainerRef }) view__overflow: ViewContainerRef;

  private inited = false;
  private meId: string;
  private initialNavigationDone = false;

  public constructor(
    private authService: AuthService,
    private modal_srv: ModalService,
    public errorNotificationService: ErrorNotificationService,
    private billingService: BillingService,
    private languageService: LanguageService,
    private configService: ConfigService,
    private loadingBar: LoadingBarService,
    private router: Router,
    private store: Store,
    private crisp: CrispChatService,
    private websocketService: WebsocketService,
    private metaService: MetaService,
    private translateService: TranslateService,
    private translationCacheService: TranslationCacheService,
    protected readonly platform: PlatformService,
  ) {
    super(platform);
  }

  protected get watchMe$(): Observable<User> {
    return this.authService.user$.pipe(
      tap((me) => {
        if (me && this.meId !== me.id) {
          this.meId = me.id;
          this.billingService.setUser(me);
        }
      }),
    );
  }

  protected get watchLanguage$(): Observable<Language> {
    return this.languageService.language$.pipe(
      tap((language) => {
        this.metaService.updateLanguage(language);
        this.store.dispatch(setLanguages({ languages: [language.code] }));
        this.configService.onLocaleChanged(language.code);
      }),
    );
  }

  protected get watchLanguages$(): Observable<Language[]> {
    return this.languageService.languages$.pipe(
      tap((languages) => {
        this.metaService.setLanguages(languages);
      }),
    );
  }

  public ngAfterViewInit(): void {
    this.modal_srv.setContainer(this.view__overflow);
  }

  public ngOnInit(): void {
    if (this.inited) {
      return;
    }
    this.initializeApp();
  }

  private initializeApp(): void {
    this.setupLanguageSubscriptions();
    this.setupWebSocketSubscriptions();
    this.setupNavigationEvents();
    this.setupTranslationSubscriptions();
    this.crisp.init();
    this.inited = true;
  }

  private setupTranslationSubscriptions(): void {
    const cache = this.translationCacheService.getCache();

    // Подписываемся на изменения в каждом языковом кэше
    cache.forEach((subject, lang) => {
      subject.pipe(takeUntil(this.destroyed$)).subscribe({
        next: (translations) => {
          if (translations) {
            this.translateService.setTranslation(lang, translations, true);
            // Принудительно обновляем текущий язык, чтобы применить новые переводы
            if (lang === this.translateService.currentLang) {
              this.translateService.use(lang);
            }
          }
        },
        error: (error) => {
          this.handleError(error, 'LOAD-SOMEDATA');
        },
      });
    });

    // Подписываемся на изменение языка
    this.languageService.language$
      .pipe(
        distinctUntilChanged((prev, curr) => prev?.code === curr?.code),
        switchMap((language) => {
          if (!language) {
            return of(null);
          }
          const subject = this.translationCacheService.getTranslationSubject(language.code);
          if (!subject) {
            return of(null);
          }
          return subject.pipe(
            map((translations) => ({ language, translations })),
            takeUntil(this.destroyed$),
          );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (result) => {
          if (result?.translations) {
            this.translateService.setTranslation(result.language.code, result.translations, true);
            this.translateService.use(result.language.code);
          }
        },
        error: (error) => {
          this.handleError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private setupLanguageSubscriptions(): void {
    combineLatest([this.watchLanguage$, this.watchLanguages$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          if (!this.isBrowser) {
            return;
          }
          this.initializeUserSubscriptions();
        },
        error: (error) => this.handleError(error, 'LOAD-SOMEDATA'),
      });
  }

  private initializeUserSubscriptions(): void {
    this.watchMe$.pipe(takeUntil(this.destroyed$)).subscribe();
    this.patchUiLanguage();
  }

  private setupWebSocketSubscriptions(): void {
    const wsSubscriptions: WebSocketSubscription[] = [
      {
        stream: this.websocketService.onAccountUpdate,
        handler: (message) => {
          const _user = { id: message.account_id } as User;
          this.authService.refreshUser(_user);
        },
      },
      {
        stream: this.websocketService.onBalanceUpdate,
        handler: () => {
          this.store.dispatch(loadBillingSetting({ force: true }));
        },
      },
      {
        stream: this.websocketService.onVerification,
        handler: (message) => {
          this.handleVerificationUpdate(message);
        },
      },
      {
        stream: this.websocketService.onReferenceUpdate,
        handler: (update) => {
          this.configService.onVersionChanged(update.entity as 'languages' | 'subjects' | 'countries', update.version);
        },
      },
    ];

    wsSubscriptions.forEach(({ stream, handler }) => {
      stream.pipe(debounceTime(5000), takeUntil(this.destroyed$)).subscribe({
        next: handler,
        error: (error) => this.handleError(error, 'SOCKET-ERROR'),
      });
    });

    this.setupWebSocketStatus();
  }

  private setupWebSocketStatus(): void {
    this.websocketService.status$
      .pipe(
        switchMap((isOnline) => {
          if (isOnline) {
            this.store.dispatch(loadProfileSettings());
          }
          return of(null);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        error: (error) => this.handleError(error, 'SOCKET-ERROR'),
      });
  }

  private handleVerificationUpdate(message: WebSocketMessage): void {
    this.store
      .select(getMyProfile)
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe({
        next: (profile) => {
          profile.verification_status = message.verification_status as unknown as UserProfileVerificationEnum;
          this.store.dispatch(setProfile({ profile }));
        },
        error: (error) => this.handleError(error, 'LOAD-SOMEDATA'),
      });
  }

  private handleError(error: any, context: SubscribeErrorType): void {
    this.errorNotificationService.captureError(error, context);
    logger.error(`Error in ${context}:`, error);
  }

  private setupNavigationEvents(): void {
    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (event) => this.updateNavigationLoadingbar(event),
      error: (error) => this.handleError(error, 'LOAD-SOMEDATA'),
    });
  }

  private patchUiLanguage(): void {
    const userLocale$ = this.watchMe$.pipe(
      filter((me): me is User => !!me),
      pluck('ui_language'),
      distinctUntilChanged(),
    );

    combineLatest([userLocale$, this.watchLanguage$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([my_locale, language]) => {
          if (my_locale === null) {
            this.languageService.changeLanguage(language, false);
          }
        },
        error: (error) => this.handleError(error, 'LOAD-SOMEDATA'),
      });
  }

  private updateNavigationLoadingbar(event: NavigationEvent): void {
    if (event instanceof NavigationStart) {
      this.loadingBar.navigationStart(25);
    }
    if (event instanceof NavigationEnd) {
      if (event.url.split('/')[1] !== 'u') {
        document.querySelector('html').scrollTop = 0;
      }
      this.loadingBar.navigationComplete();
    }
    if (event instanceof NavigationCancel || event instanceof NavigationError) {
      this.loadingBar.navigationStop();
    }
  }

  private enableAngularRouting(): void {
    if (!this.initialNavigationDone) {
      this.initialNavigationDone = true;
      this.router.initialNavigation();
    }
  }

  public get isNonWidgetPage(): boolean {
    return (
      this.router.url.includes('/profile') ||
      this.router.url.includes('/constructor') ||
      this.router.url.includes('/content/collections/edit')
    );
  }
}
